import { AccordionDetails, Button, Stack, Typography } from '@mui/material';
import { GetExportedIntegrationsResponse, GetLightInvoicesResponse } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import { isEmptyString, isNotEmptyString, ItemOf, Optional } from '@vy/utils';
import { FC } from 'react';
import { useQueryClient } from 'react-query';
import { useSyncNetSuiteInvoice } from '../../../../../../../../../../../../hooks/requests/integrations';
import { useGetNetSuiteDashboardUrl } from '../../../../../../../../../../../../hooks/requests/use-get-net-suit-dashboard-url';
import { useIntegrationStatus } from '../../../../../../../../../../../../hooks/use-integration-status';
import { queryKeys } from '../../../../../../../../../../../../services/query-key/query-keys';
import { formatDate } from '../../../../../../../../../../../../utils/date';
import { runMutation } from '../../../../../../../../../../../../utils/vy-mutation';

type NetSuiteIntegrationStatusAccordionDetailsProps = {
  netSuiteIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const NetSuiteIntegrationStatusAccordionDetails: FC<
  NetSuiteIntegrationStatusAccordionDetailsProps
> = ({ netSuiteIntegration, invoice }) => {
  const syncToNetSuiteMutation = useSyncNetSuiteInvoice();
  const { data: netSuiteInvoice } = useGetNetSuiteDashboardUrl(invoice.id);
  const { icon, label, date, isSynced } = useIntegrationStatus(netSuiteIntegration);

  const queryClient = useQueryClient();
  const invoiceId = invoice.id;
  const isApproved = invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const isSyncable = isInvoiceSyncToIntegrationAllowed(invoice);
  const disabledSync = syncToNetSuiteMutation.isLoading || !isSyncable;

  const getStatusText = () => {
    if (isSynced) {
      return formatDate(date);
    }
    if (!isApproved) {
      return 'Approve invoice to enable syncing';
    }
    if (!isSyncable) {
      return 'Child invoices sync through the parent customer';
    }
    return formatDate(date);
  };

  const handleSyncToNetSuite = async () => {
    await runMutation(
      syncToNetSuiteMutation,
      { invoicesIds: [invoiceId] },
      {
        pending: 'Requesting sync to NetSuite...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to NetSuite',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(queryKeys.invoice(invoiceId));
        },
      },
    );
  };

  const handleOpenOnNetsuite = () => {
    const url = netSuiteInvoice?.url;

    if (isNotEmptyString(url)) {
      window.open(url, '_blank');
    }
  };

  return (
    <AccordionDetails sx={{ p: 0 }}>
      <Stack
        sx={{
          gap: 2,
          mb: 1.5,
          mt: 1.5
        }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: "74px",
            flexShrink: 0,
            justifyContent: "space-between"
          }}>
          <Stack
            direction="column"
            sx={{
              alignItems: "flex-start",
              gap: "4px"
            }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1
              }}>
              <VyIcon icon={icon} strokeOpacity={0} />
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px"
                }}>
                {label}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: "20px",
                color: "#7E8796"
              }}>
              {getStatusText()}
            </Typography>
          </Stack>

          {!isSynced && (
            <Button
              variant="text"
              size="medium"
              startIcon={<VyIcon icon="Synchronize" transform="scale(0.7)" />}
              onClick={handleSyncToNetSuite}
              disabled={disabledSync}
            >
              Sync
            </Button>
          )}
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          endIcon={<VyIcon icon="OpenNew" />}
          onClick={handleOpenOnNetsuite}
          disabled={isEmptyString(netSuiteInvoice?.url)}
        >
          Open on NetSuite
        </Button>
      </Stack>
    </AccordionDetails>
  );
};
